import { useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import getLPCRegionLOS from 'lib/offer/getLPCRegionLOS'
import { getOfferValueDisplayVisibility } from 'lib/offer/getOfferDisplayPricing'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import BodyText from 'LuxKit/Typography/BodyText'
import PriceRowPriceCaptionLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceCaptionLoadingSkeleton'
import PriceRowLuxPlusPriceLoadingSkeleton from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceLoadingSkeleton'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPriceNew'
import { useSearchPrices } from 'hooks/Search/useSearchPrices'
import getOfferDurationText from 'lib/offer/getOfferDurationText'
import { formatSaleUnitCount } from 'hooks/useSaleUnitFormatting'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { buildSearchParamsKey } from 'lib/search/searchUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import { isHotelPackage } from 'lib/offer/offerTypes'
import type { LEOfferPricingComponentProps } from 'components/OfferList/OfferCards/LEOfferPriceDetails'
import { getAccommodationUnavailableLabel } from 'tripPlanner/utils/accommodationPricingDisplay'
import CarouselPricingSourceContext from './CarouselPricingSourceContext'

function ChatHotelCarouselPrice(props: LEOfferPricingComponentProps) {
  const {
    offer,
    selectedDatesAndRoomsFilter = EmptyObject,
  } = props
  const intl = useIntl()
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)

  // TRP-4453: when this card came from svc-trip's DTO, the price is already correct for the
  // requested dates/occupancy — skip the live re-fetch that useOffersBestPrices would otherwise
  // trigger. Every OTHER use of selectedDatesAndRoomsFilter below (hasDates, duration text, the
  // Always-On no-dates gate) stays on the real filter — only the fetch-triggering input changes.
  const trustDtoPrice = useContext(CarouselPricingSourceContext)
  const liveRefetchFilter = trustDtoPrice ? EmptyObject : selectedDatesAndRoomsFilter

  const bestPkg = useOffersBestPrices(offer, liveRefetchFilter)
  const pkg = bestPkg?.bestPricePackage ?? offer.lowestPricePackage!

  // For a dated search the live availability rate carries the correct fee/region price,
  // while the raw package's static price is 0 for Always-On offers (rendering "A$0"). When a
  // dated rate is loaded, price from it via the same hook the offer page + search tiles use
  // (useSearchPrices) so this card matches them. No-date renders keep the package pricing.
  const bestPrices = useAppSelector((state) => state.offer.offerBestPrices[offer.id])
  const searchKey = useMemo(
    () => buildSearchParamsKey(liveRefetchFilter.checkIn, liveRefetchFilter.checkOut, liveRefetchFilter.rooms),
    [liveRefetchFilter],
  )
  const datedBestPrice = bestPrices?.[searchKey]
  // On the trust path `liveRefetchFilter` is empty, so `searchKey` is always the no-dates
  // `"--"`. No dispatcher writes that key today — all four gate on concrete check-in/check-out
  // — so `datedBestPrice` is undefined there by that invariant rather than by construction.
  // The `!trustDtoPrice` conjunct makes it construction: were a dispatcher ever to write
  // `"--"`, an unrelated no-dates rate would otherwise silently outrank the DTO price this
  // path exists to trust.
  const datedRate = !trustDtoPrice && datedBestPrice?.available ? datedBestPrice.rate : undefined
  // While a dated rate is still being fetched we have no correct price yet: the raw package
  // falls back to lowestPricePackage (0 for Always-On, or a stale no-date lead for flash).
  // Show a skeleton instead of that wrong price. No-date renders never fetch, so loading is
  // falsy and this gate leaves homepage/wishlist untouched.
  const isLoadingDatedPrice = !!bestPkg?.loading && !datedRate
  // A dated lookup that has resolved without an available rate (sold out / errored /
  // explicitly unavailable) has no correct price — show an unavailable label rather than
  // fall through to the package price (0 for Always-On, which renders "A$0").
  const isDatedLookupUnavailable = !datedRate && !bestPkg?.loading &&
    ((datedBestPrice != null && !datedBestPrice.available) || !!bestPkg?.soldOut || !!bestPkg?.error)

  // Always-On/SPP prices are date-dependent — the offer page and search/drawer withhold the
  // price when no dates are selected rather than show the static package price as a "from"
  // lead. Flash hotels have a legit no-date "from" lead and are unaffected by this gate.
  const hasDates = !!(selectedDatesAndRoomsFilter.checkIn && selectedDatesAndRoomsFilter.checkOut)
  const isAlwaysOnWithoutDates = offer.type === OFFER_TYPE_ALWAYS_ON && !hasDates

  // svc-trip answers `status: 'ok'` with the price legs stripped whenever it could not price
  // the requested stay — an expired purchase window, an invalid occupancy or a sold-out stay
  // all produce that `unpricedSummaryPricing` shape. `mapPrice` turns it into `undefined` and
  // `mapOfferDetailToCarouselOffer` has to floor that to 0, because `App.HotelPackage.price`
  // is a required number, so 0 here means "svc-trip withheld a price", not "free".
  // `isLoadingDatedPrice` and `isDatedLookupUnavailable` cannot catch it on this path: the
  // live re-fetch is disabled, so `getOffersBestPrices` takes its no-dates branch and
  // `loading`, `soldOut` and `error` are all falsy by construction. Detail-level unavailability —
  // the BATCH-level `status: 'error'` is a different thing, handled by ChatHotelCarouselCard.
  const isDtoPriceWithheld = trustDtoPrice && pkg.price <= 0
  // `mapOfferDetailToCarouselOffer` maps the DTO's `sold_out` reason onto this field, which
  // nothing in the hotel-card path read until now. `in` rather than a cast because the prop's
  // union includes the *Summary shapes, which do not declare it.
  const isDtoSoldOut = 'isSoldOut' in offer && offer.isSoldOut === true

  const packagePricing = useOfferPackagePrice(pkg, offer)
  // Deliberately the REAL filter, not `liveRefetchFilter`: this hook only prices off `rate`,
  // and on the trust path `datedRate` is undefined by construction (see above), so its output
  // is never selected below. It dispatches nothing, so the real filter cannot trigger the
  // re-fetch this path exists to avoid. If a future change ever lets `datedRate` be defined
  // while trusting the DTO, this call becomes live and the two filters must be reconciled.
  const searchPricing = useSearchPrices({ offer, pkg, rate: datedRate, filters: selectedDatesAndRoomsFilter })

  const defaultPricing = datedRate ? searchPricing.defaultPricing : packagePricing.default
  const memberPricing = datedRate ? searchPricing.memberPricing : packagePricing.member
  const { saleUnit, price, value, discountPercent } = defaultPricing

  const valueToDisplay = canViewLuxPlusBenefits && memberPricing?.value ? memberPricing.value : value
  const discountToDisplay = canViewLuxPlusBenefits && memberPricing ? memberPricing.discountPercent : discountPercent

  const { shouldShowDiscount } = getOfferValueDisplayVisibility({
    discountPercent: discountToDisplay,
    isDiscountPillHidden: offer.isDiscountPillHidden,
  })

  const shouldShowValue = pkg.value > 0 && price && value > price && pkg.shouldDisplayValue &&
    (offer.type == OFFER_TYPE_HOTEL || (isHotelPackage(pkg) && !pkg.roomRate?.inclusionsHideValue))

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  // The package-match precondition asks "is there a bookable package of exactly this length",
  // which is a real question on the client-fetched path. A svc-trip-sourced card carries one
  // synthetic package for the priced stay and no options list, so the question is unanswerable
  // there — it is answered upstream instead, and `mapOfferDetailToCarouselOffer` withholds
  // `property.currentRegionMarketedLos` unless the answer was yes. Scoped to that source so the
  // client-fetched path keeps deciding as before.
  //
  // NOTE the caption never reads `offer.minDuration`. An earlier version of this comment said it
  // could "stay the priced stay", which stopped being true once the mapper started reading
  // svc-trip's `minDurationNights` — it is now the shortest bookable stay, which is what the
  // add-to-trip floor wants. The caption is unaffected for a reason that does not depend on that
  // value at all: `staysFrom` is reassigned to `LPCRegionLOS` whenever `LPCRegionLOS` is truthy
  // on this path, and `showStaysFromLabel` requires `LPCRegionLOS` truthy — so `minDuration`
  // never reaches the `staysFrom < pkg.duration` comparison.
  if (LPCRegionLOS && (trustDtoPrice || offer.packages.some(p => p.duration === LPCRegionLOS))) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = staysFrom && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && staysFrom < pkg.duration
  // svc-trip prices the WHOLE requested stay: for 2 rooms x 2 adults it answers `amount: 2340`
  // with `amountBasis: 'stay_total'`, exactly twice the 1-room amount. Rendered with the
  // per-unit sale-unit suffix that reads "A$2,210 /room" when the real per-room figure is half
  // that — the number correct, the unit doubled. The offer page says "3 nights, 2 rooms from"
  // for the same query, so this reuses that wording and drops the suffix.
  //
  // Keyed on the requested room count, not on `amountBasis`: the DTO reports `stay_total` for a
  // single room too, where "total" and "per room" are the same figure and the existing per-room
  // label is right. Scoped to the svc-trip-priced path — the client-fetched path prices through
  // useSearchPrices and is left exactly as it was.
  const requestedRoomCount = selectedDatesAndRoomsFilter.rooms?.length ?? 1
  // No per-person sale-unit guard: this surface caters for flash, bedbank and LPC only, all of
  // which are priced per unit of accommodation, so `formatSaleUnitCount`'s guest/adult branches
  // are unreachable here. Raised in review against tours/rentals, which this carousel does not
  // carry — the trust path only ever holds hotel/always_on/ultralux offers.
  const offerSaleUnit = offer.saleUnit ?? 'room'
  const isMultiRoomDtoTotal = trustDtoPrice && requestedRoomCount > 1

  const durationText = useMemo(() => {
    // `&& pkg.duration`: getOfferDurationText — which this replaces on the multi-room path —
    // guards a falsy duration and degrades to tileDurationLabel / LPCRegionLOS / "From". Feeding
    // it straight into an ICU plural bypassed that and rendered "0 nights, 2 rooms from". The
    // suffix stays suppressed either way: the amount is still an all-rooms total.
    if (isMultiRoomDtoTotal && pkg.duration) {
      return intl.formatMessage(
        {
          id: '5uXu2rStDOh-_a_qjyMM',
          defaultMessage: '{duration, plural, one {# night} other {# nights}}, {roomCountText} from',
          description: 'Duration label with room count, e.g. "3 nights, 2 rooms from"',
        },
        {
          duration: pkg.duration,
          roomCountText: formatSaleUnitCount(intl, offerSaleUnit, requestedRoomCount),
        },
      )
    }
    return getOfferDurationText(intl, offer, pkg)
  }, [intl, offer, pkg, isMultiRoomDtoTotal, requestedRoomCount, offerSaleUnit])

  if (isLoadingDatedPrice) {
    return <div data-testid="offer-pricing-skeleton">
      <PriceRowPriceCaptionLoadingSkeleton width="8ch" />
      <PriceRowLuxPlusPriceLoadingSkeleton size="L" />
    </div>
  }

  if (isDatedLookupUnavailable) {
    return <BodyText variant="large" weight="bold">
      <FormattedMessage
        id="2vAx_-AMvDvxXp2q_ChZ"
        defaultMessage="Price unavailable"
        description="Label shown when planned item hotel pricing is unavailable"
      />
    </BodyText>
  }

  if (isAlwaysOnWithoutDates) {
    return null
  }

  // Ordered after the Always-On gate on purpose, and pinned by a test because nothing else
  // notices the swap: svc-trip returns `dates_required` for every Always-On/SPP request without
  // dates, so EVERY Always-On card in this carousel has `pkg.price === 0` until the user picks
  // some. Those cards must keep rendering nothing here so the card's own "Select dates" CTA is
  // what shows — a "Price unavailable" label would claim svc-trip tried and failed when it was
  // never asked for a dated price.
  //
  // The copy comes from the shared helper rather than the message above, so this card says
  // "Sold Out" for a sold-out stay exactly like DrawerHotelTile, ChatMapOfferCard and
  // HotelDetailPricing — the three other surfaces that render this same DTO's reason. The gate
  // above keeps its own id: it belongs to the client-fetched path, which has no DTO reason to
  // distinguish, and shares that string with ChatHotelCarouselCard's batch-failure card.
  if (isDtoPriceWithheld) {
    return <BodyText variant="large" weight="bold">
      {getAccommodationUnavailableLabel(isDtoSoldOut ? 'sold_out' : 'unavailable', intl)}
    </BodyText>
  }

  return (<>
    <PriceRowPriceCaption>{durationText}</PriceRowPriceCaption>
    <LuxPlusPriceStack
      price={price}
      memberPrice={memberPricing?.price}
      size="L"
      // Omitted for a multi-room total: the caption above already says "2 rooms from", and a
      // "/room" suffix on the same figure would contradict it.
      saleUnit={isMultiRoomDtoTotal ? '' : saleUnit}
      offerConfig={offer.luxPlus}
      testid="main-offer-pricing"
    />
    {shouldShowValue && <PriceRowValueDiscountWithCaption
      size="L"
      originalValue={Math.ceil(valueToDisplay)}
      discountPercentage={shouldShowDiscount ? discountToDisplay : undefined}
      data-testid="le-offer-pricing"
    />}
    {staysFrom && showStaysFromLabel && <PriceRowCaption>
      <FormattedMessage
        id="7PFPr3tSopW8Hgmmb_Dy"
        defaultMessage="Stays from {staysFrom, plural, one {# night} other {# nights}} available"
        description="Caption indicating minimum stay duration available for an always-on offer"
        values={{ staysFrom }}
      />
    </PriceRowCaption>}
    {!!pkg.roomOccupancy && offer.type === 'tour' && <PriceRowCaption>
      {pkg.roomOccupancy}
    </PriceRowCaption>}
  </>)
}

export default ChatHotelCarouselPrice
